import { useDeviceType } from '@cian/ui-kit/responsive';
import * as React from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { handlePinPaginationClick } from './handlePinPaginationClick';
import { trackPinClick } from './trackPinClick';
import { trackPinListingShow } from './trackPinListingShow';
import { useHotspotClickHandler } from './useHotspotClickHandler';
import { useMaxPinsPerCluster } from './useMaxPinsPerCluster';
import { IPinsLayer } from './usePinsLayer';
import { closeMobileListing } from '../../../../common/actions/listingModal';
import { openOfferPreview } from '../../../../common/actions/openOfferPreview/openOfferPreview';
import { markPinAsViewed } from '../../../../common/actions/viewed/markPinAsViewed';
import { EPinAction, PIN_ACTION_ATTR } from '../../../../common/constants/pinActions';
import { selectIsMultipinsExpActive } from '../../../../common/selectors/experiments';
import { TFeature } from '../../../../common/types/map';
import { IApplicationState, TThunkDispatch } from '../../../../common/types/redux';
import { expandClusterPinFeature } from '../../../../common/utils/features';
import { getPinCounterLabel } from '../../../../common/utils/ymapsModules/BalloonManager/getPinCounterLabel';
import { useMapController } from '../../../controller/useMapController';
import { selectActiveFeature, selectMapBounds } from '../../../selectors';
import { TMapBounds } from '../../../types/mapBounds';

/** Касания, которые ymaps не должен видеть, когда они начались на кнопке пагинации (target у всех - кнопка). */
const PAGINATION_TOUCH_EVENTS = ['touchstart', 'touchmove', 'touchend', 'touchcancel'] as const;

/** Направление листания, если событие пришло из кнопки пагинации мультипина. */
function getPaginationDirection(target: EventTarget | null): 'prev' | 'next' | null {
  if (!(target instanceof Element)) {
    return null;
  }

  const action = target.closest(`[${PIN_ACTION_ATTR}]`)?.getAttribute(PIN_ACTION_ATTR);
  if (action === EPinAction.PrevOffer) {
    return 'prev';
  }

  return action === EPinAction.NextOffer ? 'next' : null;
}

interface IUsePinsClickHandlersParams extends IPinsLayer {
  map: YMaps.Map;
  isResultsAvailable: boolean;
  isDetailsVisible: boolean;
}

export function usePinsClickHandlers({
  map,
  hotspotsLayer,
  balloonManager,
  isResultsAvailable,
  isDetailsVisible,
}: IUsePinsClickHandlersParams): void {
  const dispatch = useDispatch<TThunkDispatch>();
  const { getState } = useStore<IApplicationState>();
  const deviceType = useDeviceType();
  const maxPinsPerCluster = useMaxPinsPerCluster();
  const controller = useMapController();
  const { precision } = useSelector<IApplicationState, TMapBounds>(selectMapBounds);

  React.useEffect(() => {
    const mapElement = map.container.getParentElement();
    const handlePaginationClick = (event: MouseEvent) => {
      const direction = getPaginationDirection(event.target);
      if (!direction) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      handlePinPaginationClick(direction, deviceType, { dispatch, getState });
    };

    // На touch-устройствах ymaps перехватывает касания в контейнере карты: гасит touchstart, а из
    // одинокого touchend делает свой contextmenu и гасит и его - браузер тогда не рождает click по кнопке.
    // Касание пагинации до ymaps не доводим целиком, и click приходит как обычно.
    const keepPaginationTouch = (event: TouchEvent) => {
      if (getPaginationDirection(event.target)) {
        event.stopPropagation();
      }
    };

    mapElement.addEventListener('click', handlePaginationClick, true);
    PAGINATION_TOUCH_EVENTS.forEach(type => mapElement.addEventListener(type, keepPaginationTouch, true));

    return () => {
      mapElement.removeEventListener('click', handlePaginationClick, true);
      PAGINATION_TOUCH_EVENTS.forEach(type => mapElement.removeEventListener(type, keepPaginationTouch, true));
    };
  }, [map, getState, dispatch, deviceType]);

  // Клик по "веснушкам" -> зум в область веснушки
  const handleHotspotClick = useHotspotClickHandler({ isResultsAvailable, isDetailsVisible });

  React.useEffect(() => {
    hotspotsLayer.layer.events.add('click', handleHotspotClick);

    return () => {
      hotspotsLayer.layer.events.remove('click', handleHotspotClick);
    };
  }, [hotspotsLayer, handleHotspotClick]);

  // Клик по пинам с ценами -> Открытие превью оффера на карте
  const handleBalloonClick = React.useCallback(
    (event: YMaps.IEvent) => {
      const objectId = event.get<string>('objectId');
      const feature = balloonManager.objectManager.objects.getById<TFeature>(objectId);

      if (!feature) {
        return;
      }

      const offerId = feature.properties.offers?.[0]?.cianId;
      const currentZoom = controller.getZoom();
      const zoom = Math.round(currentZoom);

      trackPinClick({ isDetailsVisible, offerId, zoom });

      // Клик по счётчику или повторный клик по выбранному пину сохраняет текущий оффер.
      const activeFeature = selectActiveFeature(getState());
      if (activeFeature?.id === feature.id && activeFeature.precision === precision) {
        return;
      }

      // ObjectManager привязан к центру кластера; превью нужна точка самого пина
      // с пиксельным смещением слота на текущем зуме.
      const expandedFeature = expandClusterPinFeature(feature, currentZoom, maxPinsPerCluster);
      if (!expandedFeature) {
        return;
      }

      // Выдача пина - только когда он открывается: повторный клик по выбранному пину выходит выше
      trackPinListingShow(feature.properties.offers, getState(), deviceType);

      // closeMobileListing - мобильная концепция: сбрасывает офферы общего листинга.
      // На десктопе листинг живёт и его трогать нельзя, иначе клик по пину чистит выдачу.
      if (deviceType !== 'desktop') {
        dispatch(closeMobileListing());
      }
      const isPhone = deviceType === 'phone';
      // Шторка мультипина на телефоне отмечает пин сама - при развороте на весь экран (дизайн-ревью 2026-10-07)
      const isMultipinSheet = isPhone && getPinCounterLabel(expandedFeature.properties) !== null;
      const isPinViewedOnOpen = selectIsMultipinsExpActive(getState()) && !isMultipinSheet;
      dispatch(
        openOfferPreview(expandedFeature, {
          precision,
          isDetailed: isDetailsVisible,
          isPhone,
          onPinOpen: isPinViewedOnOpen ? pin => dispatch(markPinAsViewed(pin)) : undefined,
        }),
      );
    },
    [balloonManager, controller, dispatch, deviceType, precision, isDetailsVisible, getState, maxPinsPerCluster],
  );

  React.useEffect(() => {
    balloonManager.objectManager.objects.events.add('click', handleBalloonClick);

    return () => {
      balloonManager.objectManager.objects.events.remove('click', handleBalloonClick);
    };
  }, [balloonManager, handleBalloonClick]);
}
